import { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Kpi, StatusBadge } from "../components/Kpi";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Phone, PhoneCall, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const DISPOSITIONS = ["connected","interested","hot","warm","cold","call_back","no_answer","busy","switched_off","visit_planned","not_interested","wrong_number","already_purchased","future_prospect"];

export default function PreSales() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [disp, setDisp] = useState("connected");
  const [remarks, setRemarks] = useState("");
  const [mode, setMode] = useState("call");

  const load = () => api.get("/presales/queue").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!active) return;
    try {
      const { data } = await api.post("/presales/disposition", { lead_id: active.id, disposition: disp, remarks, mode });
      toast.success(`Saved. Next follow-up: ${data.next_followup_at ? new Date(data.next_followup_at).toLocaleString() : "—"}`);
      setActive(null); setRemarks(""); setDisp("connected");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const renderList = (list) => (
    <div className="divide-y divide-border">
      {list.map(l => (
        <button key={l.id} onClick={() => setActive(l)} data-testid={`queue-lead-${l.lead_code}`}
          className={`w-full text-left px-4 py-3 hover:bg-accent/10 flex items-center gap-3 ${active?.id === l.id ? "bg-accent/10" : ""}`}>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[11px] text-muted-foreground">{l.lead_code}</span>
              <StatusBadge status={l.stage}/>
            </div>
            <div className="mt-1">{l.client_name}</div>
            <div className="text-xs text-muted-foreground font-mono">{l.mobile}</div>
          </div>
          <ChevronRight size={16} className="text-muted-foreground"/>
        </button>
      ))}
      {list.length === 0 && <div className="px-4 py-10 text-center text-muted-foreground text-sm">Nothing in this queue.</div>}
    </div>
  );

  if (!data) return <div className="text-muted-foreground">Loading calling desk…</div>;

  return (
    <div className="space-y-6 animate-in">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Sales CRM</div>
        <h1 className="font-serif-display text-3xl">Pre-Sales Calling Desk</h1>
        <p className="text-sm text-muted-foreground mt-1">Every disposition automatically schedules the next follow-up — nothing slips.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi label="New Untouched" value={data.counts.new_untouched} testId="ps-new"/>
        <Kpi label="Today's Follow-ups" value={data.counts.todays_followups} testId="ps-today"/>
        <Kpi label="Overdue" value={data.counts.overdue} sub="Action needed" testId="ps-overdue"/>
        <Kpi label="Hot / Interested" value={data.counts.hot} testId="ps-hot"/>
        <Kpi label="Visit Scheduled" value={data.counts.visit_scheduled} testId="ps-visit"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <Tabs defaultValue="overdue">
            <TabsList className="w-full">
              <TabsTrigger value="overdue" data-testid="ps-tab-overdue" className="flex-1">Overdue <span className="ml-1 text-xs">({data.counts.overdue})</span></TabsTrigger>
              <TabsTrigger value="today" data-testid="ps-tab-today" className="flex-1">Today <span className="ml-1 text-xs">({data.counts.todays_followups})</span></TabsTrigger>
              <TabsTrigger value="new" data-testid="ps-tab-new" className="flex-1">New <span className="ml-1 text-xs">({data.counts.new_untouched})</span></TabsTrigger>
            </TabsList>
            <TabsContent value="overdue" className="mt-0">{renderList(data.queue_overdue)}</TabsContent>
            <TabsContent value="today" className="mt-0">{renderList(data.queue_today)}</TabsContent>
            <TabsContent value="new" className="mt-0">{renderList(data.queue_new)}</TabsContent>
          </Tabs>
        </Card>

        <Card className="lg:col-span-2 p-6">
          {!active ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
              <div className="text-center">
                <PhoneCall className="mx-auto mb-3 opacity-40" size={32}/>
                Select a lead from the queue to start the call.
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Active Call</div>
                  <div className="font-serif-display text-2xl mt-1">{active.client_name}</div>
                  <div className="font-mono text-sm mt-1">{active.mobile}</div>
                  <div className="text-xs text-muted-foreground mt-1">{active.lead_code} · {active.configuration || "—"} · {active.preferred_location || ""}</div>
                </div>
                <a href={`tel:${active.mobile}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm" data-testid="call-btn">
                  <Phone size={14}/> Call
                </a>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-6">
                <div>
                  <Label>Mode</Label>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger data-testid="ps-mode"><SelectValue/></SelectTrigger>
                    <SelectContent>{["call","whatsapp","email","video","other"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Disposition</Label>
                  <Select value={disp} onValueChange={setDisp}>
                    <SelectTrigger data-testid="ps-disposition"><SelectValue/></SelectTrigger>
                    <SelectContent className="max-h-64 overflow-auto">
                      {DISPOSITIONS.map(d => <SelectItem key={d} value={d}>{d.replace(/_/g," ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Label>Remarks</Label>
                <Textarea rows={3} value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Note key points from the conversation…" data-testid="ps-remarks"/>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                <b>Auto rule:</b> next follow-up will be scheduled automatically based on the disposition.
              </div>
              <div className="mt-4 flex gap-2">
                <Button onClick={submit} data-testid="ps-submit">Save Disposition & Continue</Button>
                <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
